import windows from "./windows"
import settings from "./settings"
import { parseJSON } from "@/utils/json"
import { localStore } from "./store"
import logger from "./logger"

interface Attribution {
  ref: string
  src?: string
  refSrc?: string
}

type SerializedAttribution = Attribution & {
  linkAttributions?: PerLinkAttributions
}

export type PerLinkAttributions = Record<string, Record<string, string>>

type StoredAttributions = Record<
  string,
  {
    attribution: Attribution
    ts: number
    referrer: string
  }
>

const storageKey = "nosto:attribution"
const dataRef = "data-nosto-ref"
const attributionDuration = 3 * 60 * 1000
let currentAttributionLocation: string
let currentAttributionValue: { attribution: Attribution }

function w() {
  return windows.site
}

function locationUrl() {
  return normalizeUrl(w().location.href)
}

function canonicalUrl() {
  const link = w().document.querySelector<HTMLLinkElement>("link[rel='canonical']")
  return link ? normalizeUrl(link.getAttribute("href") || "") : undefined
}

function normalizeUrl(url: string) {
  if (!url) {
    return url
  }
  try {
    const u = new URL(url, window.location.href)
    u.hash = ""
    if (settings.parameterlessAttributionNoQueryCheck) {
      u.search = ""
    }
    return u.toString()
  } catch (e) {
    logger.warn("Failed to normalize URL", e)
    return url
  }
}

function read() {
  const json = localStore.get(storageKey)
  const parsed = json ? parseJSON<StoredAttributions>(json) : {}
  // cleanup stale attribution
  const now = Date.now()
  return Object.fromEntries(Object.entries(parsed).filter(([_, { ts }]) => now - ts <= attributionDuration))
}

export function putAttribution(url: string, attribution: Attribution | undefined) {
  const serialized = JSON.stringify({
    ...read(),
    [normalizeUrl(url)]: attribution && {
      attribution,
      ts: Date.now(),
      referrer: locationUrl()
    }
  })
  // empty attribution should lead to removal of storage item
  if (serialized === "{}") {
    localStore.remove(storageKey)
  } else {
    try {
      localStore.set(storageKey, serialized)
    } catch (e) {
      logger.warn("Attribution storage failed", e)
      throw e
    }
  }
}

function attributionByUrl() {
  const data = read()
  const location = locationUrl()
  const canonical = canonicalUrl()

  const urls = [location]

  if (canonical && canonical !== location) {
    urls.push(canonical)
  }

  const { url, attr } = urls.map(url => ({ url, attr: data[url] })).find(({ attr }) => !!attr) || {}

  if (url && attr) {
    return { url, attr }
  }

  if (!settings.site || !settings.extraHosts?.length) {
    return
  }

  // using front page url and extra hosts
  const domains = [new URL(settings.site).hostname, ...settings.extraHosts]

  return domains
    .flatMap(host =>
      urls.map(url => {
        const parsedUrl = new URL(url)
        parsedUrl.hostname = host
        return parsedUrl.toString()
      })
    )
    .map(url => ({ url, attr: data[url] }))
    .find(({ attr }) => !!attr)
}

export function readAttribution(skipReferrer: boolean = false) {
  const { url, attr } = attributionByUrl() || {}
  const referrer = normalizeUrl(w().document.referrer)
  if (attr && (skipReferrer || !referrer || attr.referrer === referrer)) {
    currentAttributionValue = attr
    currentAttributionLocation = url!
  }
  if (url) {
    putAttribution(url, undefined)
  }
}

export function currentAttribution(): Attribution {
  // if location is changed without a reload it is worth reloading the attribution
  if (locationUrl() !== currentAttributionLocation) {
    // don't check for referrer as there was no page reload
    readAttribution(true)
  }
  return { ...currentAttributionValue?.attribution }
}

function isAnchorElement(et: EventTarget | null): et is HTMLAnchorElement {
  if (!et) {
    return false
  }

  // Fast path for native anchor elements with href
  if (et instanceof HTMLAnchorElement && et.hasAttribute("href")) {
    return true
  }
  // Fallback for elements that may be anchors in different contexts (e.g., iframes)
  if (isHTMLElement(et)) {
    return et.tagName === "A" && et.hasAttribute("href")
  }
  return false
}

function isHTMLElement(target: EventTarget | null): target is HTMLElement {
  if (!target) {
    return false
  }
  const targetEl = target as Node
  return (
    targetEl instanceof HTMLElement ||
    // support cases where Nosto is included in a different iframe
    (targetEl &&
      typeof targetEl === "object" &&
      targetEl.nodeType === Node.ELEMENT_NODE &&
      typeof targetEl.nodeName === "string")
  )
}

function saveAttribution(event: Event) {
  if (!isHTMLElement(event.target)) {
    return
  }

  const link = event.composedPath().find(isAnchorElement)
  const targetAttributionElement = event.target.closest<HTMLElement>(`[${dataRef}]`)
  if (
    !link ||
    !targetAttributionElement ||
    (targetAttributionElement.nostoAttributionCheck && !targetAttributionElement.nostoAttributionCheck(link))
  ) {
    return
  }
  const { href } = link
  if (href.startsWith("javascript:")) {
    // skip attribution handling for javascript links
    return
  }

  const { linkAttributions, ...campaignAttribution } = parseJSON<SerializedAttribution>(
    targetAttributionElement.getAttribute(dataRef)!
  )
  putAttribution(href, { ...campaignAttribution, ...(linkAttributions || {})[href] })
}

type Check = (e: HTMLElement) => boolean

export function wrapWithAttribution(
  element: HTMLElement,
  campaignAttribution: Attribution,
  linkAttributions?: PerLinkAttributions,
  check?: Check
) {
  // non element nodes can be present here too, checking with hasAttribute
  if (element.hasAttribute && !element.hasAttribute(dataRef)) {
    if (check) {
      element.nostoAttributionCheck = check
    }
    element.addEventListener("click", saveAttribution)
    element.addEventListener("contextmenu", saveAttribution)

    const combined = linkAttributions ? { ...campaignAttribution, linkAttributions } : campaignAttribution
    if (Object.keys(combined).length) {
      element.setAttribute(dataRef, JSON.stringify(combined))
    }
  }
}
